import { guardArray } from "../array";
import { convertMinutesToHours, guardNumber } from "../numbers";
import type { AssignmentReferenceSingleBookingSchema } from "../schemas";
import type { Nullable, NullableFields } from "../types";
import { calculateBookingMinutes } from "./calculate-booking-minutes";

/**
 * This function calculates the bookings for a given data and hourly agreement.
 * @param data - The data to calculate the bookings for.
 * @param _hourlyAgreement - The hourly agreement to calculate the bookings for.
 * @returns The calculated bookings.
 */
export function calculateBookings(
	_data: Nullable<NullableFields<AssignmentReferenceSingleBookingSchema>[]>,
	_hourlyAgreement: Nullable<number>,
) {
	const data = guardArray(_data);
	const hourlyAgreement = guardNumber(_hourlyAgreement);

	const adjustments = data?.map((booking) => booking.adjustment).filter((item) => !!item);
	const submittedMinutes = calculateBookingMinutes(data);
	const submittedHours = convertMinutesToHours(submittedMinutes);
	const submittedSettlement = submittedHours * hourlyAgreement;
	const adjustedMinutes = calculateBookingMinutes(adjustments);
	const adjustedHours = convertMinutesToHours(adjustedMinutes);
	const diff = adjustedHours - submittedHours;
	const adjustedSettlement = adjustedHours * hourlyAgreement;
	const hasBookings = guardNumber(data?.length) > 0;
	const hasAdjustments = guardNumber(adjustments?.filter((item) => !!item?.result).length) > 0;
	// The host answered and the answer moved the hours — an acceptance as submitted is an answer
	// too, but there is nothing to show beside the submitted figure then.
	const hasChangedHours = hasAdjustments && adjustedMinutes !== submittedMinutes;
	const isNegativeChange = adjustedSettlement < submittedSettlement && hasAdjustments;

	return {
		submittedMinutes,
		submittedHours,
		submittedSettlement,
		adjustedMinutes,
		adjustedHours,
		diff,
		adjustedSettlement,
		hasBookings,
		hasAdjustments,
		hasChangedHours,
		isNegativeChange,
	};
}
